import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { canSalesTarget } from './salesTargetAccess';
import SalesAutomationIssues from './SalesAutomationIssues';
import './SalesAutomationLogsTab.css';

// Fallback when the automation API predates the company list in sync/status.
const DEFAULT_COMPANIES = ['BQF', 'CAP', 'BQBM', 'AHLA_MART', 'BBM'];

const STATUS_LABEL = { success: 'Success', partial: 'Partial', failed: 'Failed', skipped: 'Skipped' };

const StatusBadge = ({ status }) => (
    <span className={`sal-badge sal-badge--${status}`}>{STATUS_LABEL[status] ?? status}</span>
);

const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);
const IconSync = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2v6h-6"/>
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
        <path d="M3 22v-6h6"/>
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
    </svg>
);
const IconChevron = ({ open }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

// Backend sends naive UTC timestamps (no 'Z'/offset). Ensure they're parsed as
// UTC, then display in Qatar time so the view is correct regardless of browser TZ.
const toUtc = (ts) => {
    if (typeof ts === 'string' && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(ts)) {
        return ts.replace(' ', 'T') + 'Z';
    }
    return ts;
};
const fmt = (ts) => ts ? new Date(toUtc(ts)).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Qatar' }) : '—';
const dur = (start, end) => {
    if (!start || !end) return '—';
    const s = Math.round((new Date(end) - new Date(start)) / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

// Report dates are business dates in Qatar time, so "today" must be Qatar's
// today regardless of where the browser is. en-CA formats as YYYY-MM-DD.
const qatarToday = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Qatar', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const shiftDate = (iso, days) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

const daysBetween = (from, to) =>
    Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000) + 1;

// `initial` pre-fills the form when opened from a row in the Issues view.
const ManualSyncModal = ({ onClose, onStarted, maxDays, allCompanies, initial }) => {
    const today = qatarToday();
    const [fromDate, setFromDate] = useState(initial?.from ?? shiftDate(today, -7));
    const [toDate,   setToDate]   = useState(initial?.to   ?? shiftDate(today, -1));
    const [selected, setSelected] = useState(initial?.companies?.length ? initial.companies : allCompanies);
    const [busy,     setBusy]     = useState(false);
    const [error,    setError]    = useState(null);

    const toggleCompany = (code) => setSelected(s =>
        s.includes(code) ? s.filter(c => c !== code) : [...s, code]);
    const allSelected = selected.length === allCompanies.length;

    const span = fromDate && toDate ? daysBetween(fromDate, toDate) : 0;
    const validation =
        !fromDate || !toDate            ? 'Pick both a From and a To date'
        : span < 1                      ? "'To' date cannot be before the 'From' date"
        : toDate > today                ? "'To' date cannot be in the future"
        : span > maxDays                ? `Range covers ${span} days; the maximum per sync is ${maxDays}`
        : selected.length === 0         ? 'Pick at least one company'
        : null;

    const submit = async () => {
        if (validation) return;
        setBusy(true);
        setError(null);
        try {
            const body = { from_date: fromDate, to_date: toDate };
            // Omit the list for a full sync so the server runs every company.
            if (!allSelected) body.companies = allCompanies.filter(c => selected.includes(c));
            await axiosInstance.post('/api/sales-automation/sync', body);
            onStarted(
                `Sync started for ${allSelected ? 'all companies' : body.companies.join(', ')}, `
                + `${fromDate} → ${toDate} (${span} day${span > 1 ? 's' : ''}).`
            );
            onClose();
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to start the sync');
            setBusy(false);
        }
    };

    return (
        <div className="sal-modal-backdrop" onClick={onClose}>
            <div className="sal-modal" onClick={e => e.stopPropagation()}>
                <h3>Manual Sync</h3>
                <p className="sal-modal__hint">
                    Re-downloads the sales reports for every date in the range and uploads them
                    to Prime Office, for the companies ticked below. Runs in the background — you
                    can close this tab.
                </p>

                <div className="sal-modal__companies">
                    <div className="sal-modal__companies-head">
                        <span>Companies</span>
                        <button type="button" className="sal-link"
                            onClick={() => setSelected(allSelected ? [] : allCompanies)}>
                            {allSelected ? 'Clear all' : 'Select all'}
                        </button>
                    </div>
                    <div className="sal-modal__company-list">
                        {allCompanies.map(c => (
                            <label key={c} className={selected.includes(c) ? 'is-on' : ''}>
                                <input type="checkbox" checked={selected.includes(c)}
                                    onChange={() => toggleCompany(c)} />
                                {c}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="sal-modal__fields">
                    <label>
                        From date
                        <input
                            type="date"
                            value={fromDate}
                            max={today}
                            onChange={e => setFromDate(e.target.value)}
                        />
                    </label>
                    <label>
                        To date
                        <input
                            type="date"
                            value={toDate}
                            max={today}
                            onChange={e => setToDate(e.target.value)}
                        />
                    </label>
                </div>

                {!validation && span > 0 && (
                    <p className="sal-modal__span">
                        {span} day{span > 1 ? 's' : ''} × {selected.length} compan{selected.length === 1 ? 'y' : 'ies'} will be processed
                        {span > 7 && ' — this can take a while.'}
                    </p>
                )}
                {validation && <p className="sal-modal__invalid">{validation}</p>}
                {error && <div className="sal-error">{error}</div>}

                <div className="sal-modal__actions">
                    <button className="sal-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button
                        className="sal-btn sal-btn--primary"
                        onClick={submit}
                        disabled={busy || !!validation}
                    >
                        {busy ? 'Starting…' : 'Start Sync'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SummaryCards = ({ summary }) => (
    <div className="sal-cards">
        {[
            { label: 'Total Runs',    value: summary.total_runs    ?? '—' },
            { label: 'Success',       value: summary.success_runs  ?? '—', mod: 'success' },
            { label: 'Partial',       value: summary.partial_runs  ?? '—', mod: 'partial' },
            { label: 'Failed',        value: summary.failed_runs   ?? '—', mod: 'failed' },
            { label: 'Last Run',      value: summary.last_run_at ? fmt(summary.last_run_at) : '—' },
        ].map(c => (
            <div key={c.label} className={`sal-card ${c.mod ? 'sal-card--' + c.mod : ''}`}>
                <span className="sal-card__value">{c.value}</span>
                <span className="sal-card__label">{c.label}</span>
            </div>
        ))}
    </div>
);

const CompanyResults = ({ results }) => (
    <table className="sal-detail-table">
        <thead>
            <tr>
                <th>Company</th>
                <th>File</th>
                <th>Date</th>
                <th>Download</th>
                <th>Upload</th>
                <th>Salesmen (OK / Failed)</th>
            </tr>
        </thead>
        <tbody>
            {results.map(r => (
                <React.Fragment key={r.id}>
                    <tr>
                        <td><code>{r.company_code}</code></td>
                        <td className="sal-filename">{r.file_name}</td>
                        <td>{r.report_date ?? '—'}</td>
                        <td>
                            <StatusBadge status={r.download_status} />
                            {r.download_error && <span className="sal-err-tip" title={r.download_error}>!</span>}
                        </td>
                        <td>
                            <StatusBadge status={r.upload_status} />
                            {r.upload_error && <span className="sal-err-tip" title={r.upload_error}>!</span>}
                        </td>
                        <td className="sal-counts">
                            {r.salesmen_success ?? '—'} / {r.salesmen_failed ?? '—'}
                        </td>
                    </tr>
                    {r.row_errors && r.row_errors.length > 0 && (
                        <tr>
                            <td colSpan={6} style={{ background: '#fef2f2', padding: '8px 12px', fontSize: '12px', color: '#991b1b' }}>
                                <strong>Errors:</strong>
                                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                    {r.row_errors.map((err, i) => <li key={i}>{err}</li>)}
                                </ul>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            ))}
        </tbody>
    </table>
);

const RunRow = ({ run }) => {
    const [open,    setOpen]    = useState(false);
    const [detail,  setDetail]  = useState(null);
    const [loading, setLoading] = useState(false);

    const toggle = async () => {
        if (!open && !detail) {
            setLoading(true);
            try {
                const res = await axiosInstance.get(`/api/sales-automation/runs/${run.id}`);
                setDetail(res.data.data);
            } catch { /* ignore */ }
            setLoading(false);
        }
        setOpen(o => !o);
    };

    return (
        <>
            <tr className={`sal-run-row sal-run-row--${run.status}`} onClick={toggle} style={{ cursor: 'pointer' }}>
                <td><IconChevron open={open} /></td>
                <td><span className={`sal-type-tag sal-type-tag--${run.run_type}`}>{run.run_type}</span></td>
                <td>{fmt(run.started_at)}</td>
                <td>{dur(run.started_at, run.finished_at)}</td>
                <td><StatusBadge status={run.status} /></td>
                <td className="sal-counts">{run.downloads_ok} / {(run.downloads_ok ?? 0) + (run.downloads_fail ?? 0)}</td>
                <td className="sal-counts">{run.uploads_ok} / {(run.uploads_ok ?? 0) + (run.uploads_fail ?? 0)}</td>
                <td className="sal-error-cell" title={run.error_message ?? ''}>
                    {run.error_message ? run.error_message.slice(0, 60) + (run.error_message.length > 60 ? '…' : '') : '—'}
                </td>
            </tr>
            {open && (
                <tr className="sal-detail-row">
                    <td colSpan={8}>
                        {loading && <p className="sal-loading">Loading…</p>}
                        {detail && <CompanyResults results={detail.company_results} />}
                    </td>
                </tr>
            )}
        </>
    );
};

const SalesAutomationLogsTab = () => {
    const { user } = useAuth();
    // Starting a sync is a POST — the card's 'create' action on the server.
    const canSync = canSalesTarget(user, 'create');
    const [summary,  setSummary]  = useState(null);
    const [runs,     setRuns]     = useState([]);
    const [total,    setTotal]    = useState(0);
    const [page,     setPage]     = useState(1);
    const [filter,   setFilter]   = useState('');
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);
    const [syncOpen, setSyncOpen] = useState(false);
    const [syncInit, setSyncInit] = useState(null);
    const [sync,     setSync]     = useState(null);
    const [notice,   setNotice]   = useState(null);
    const [view,     setView]     = useState('runs');
    // Bumped when a sync finishes so the Issues view reloads too.
    const [refreshKey, setRefreshKey] = useState(0);
    const wasRunning = React.useRef(false);
    // A just-spawned run needs a few seconds to take the lock; until then the
    // API still reports idle and we must not call the sync finished.
    const pendingUntil = React.useRef(0);
    const LIMIT = 20;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: LIMIT };
            if (filter) params.run_type = filter;
            const [runsRes, summRes] = await Promise.all([
                axiosInstance.get('/api/sales-automation/runs', { params }),
                axiosInstance.get('/api/sales-automation/summary'),
            ]);
            setRuns(runsRes.data.data?.data   ?? []);
            setTotal(runsRes.data.data?.total ?? 0);
            setSummary(summRes.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load automation logs');
        }
        setLoading(false);
    }, [page, filter]);

    useEffect(() => { load(); }, [load]);

    const loadSyncStatus = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/sales-automation/sync/status');
            const status = res.data.data;

            if (!status.running && Date.now() < pendingUntil.current) {
                setSync({ ...status, running: true });   // still starting up
                return;
            }

            setSync(status);
            // A run that just finished has fresh rows waiting for us.
            if (wasRunning.current && !status.running) {
                setNotice('Sync finished — the run is listed below.');
                load();
                setRefreshKey(k => k + 1);
            }
            wasRunning.current = !!status.running;
        } catch {
            setSync(s => s ?? { unavailable: true });
        }
    }, [load]);

    // Poll faster while a run is in flight so the banner clears promptly.
    useEffect(() => {
        loadSyncStatus();
        const ms = sync?.running ? 15000 : 60000;
        const id = setInterval(loadSyncStatus, ms);
        return () => clearInterval(id);
    }, [loadSyncStatus, sync?.running]);

    const running   = !!sync?.running;
    const syncReady = !!sync && !sync.unavailable;
    const totalPages = Math.ceil(total / LIMIT);
    const allCompanies = sync?.companies ?? DEFAULT_COMPANIES;

    const openSync = (initial = null) => {
        setNotice(null);
        setSyncInit(initial);
        setSyncOpen(true);
    };

    return (
        <div className="sal-tab">
            <div className="sal-header">
                <div>
                    <h2>Automation Logs</h2>
                    <p>History of daily &amp; weekly report automation runs</p>
                </div>
                <div className="sal-header-actions">
                    <div className="sal-view-toggle">
                        <button className={view === 'runs' ? 'is-active' : ''} onClick={() => setView('runs')}>
                            Runs
                        </button>
                        <button className={view === 'issues' ? 'is-active' : ''} onClick={() => setView('issues')}>
                            Issues by company
                        </button>
                    </div>
                    {view === 'runs' && (
                        <select
                            className="sal-filter"
                            value={filter}
                            onChange={e => { setFilter(e.target.value); setPage(1); }}
                        >
                            <option value="">All types</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="manual">Manual</option>
                        </select>
                    )}
                    {canSync && (
                        <button
                            className="sal-sync-btn"
                            onClick={() => openSync()}
                            disabled={!syncReady || running}
                            title={
                                !syncReady ? 'Automation service unavailable'
                                : running  ? 'An automation run is already in progress'
                                : 'Re-run the automation for a date range'
                            }
                        >
                            <IconSync /> Manual Sync
                        </button>
                    )}
                    <button className="sal-refresh-btn" disabled={loading}
                        onClick={() => { load(); setRefreshKey(k => k + 1); }}>
                        <IconRefresh /> Refresh
                    </button>
                </div>
            </div>

            {running && (
                <div className="sal-banner sal-banner--running">
                    <span className="sal-spinner" />
                    <span>
                        Automation in progress
                        {sync.current?.owner ? ` — ${sync.current.owner}` : ''}
                        {sync.current?.detail ? ` (${sync.current.detail})` : ''}
                        {sync.current?.started_at ? `, started ${fmt(sync.current.started_at)}` : ''}
                    </span>
                </div>
            )}

            {notice && !running && (
                <div className="sal-banner sal-banner--info">
                    <span>{notice}</span>
                    <button className="sal-banner__close" onClick={() => setNotice(null)}>×</button>
                </div>
            )}

            {summary && view === 'runs' && <SummaryCards summary={summary} />}

            {error && <div className="sal-error">{error}</div>}

            {syncOpen && (
                <ManualSyncModal
                    maxDays={sync?.max_days ?? 92}
                    allCompanies={allCompanies}
                    initial={syncInit}
                    onClose={() => setSyncOpen(false)}
                    onStarted={(msg) => {
                        setNotice(msg);
                        wasRunning.current = true;
                        pendingUntil.current = Date.now() + 30000;
                        setSync(s => ({ ...(s || {}), running: true }));
                    }}
                />
            )}

            {view === 'issues' ? (
                <SalesAutomationIssues
                    canSync={canSync}
                    syncDisabled={!syncReady || running}
                    onResync={openSync}
                    refreshKey={refreshKey}
                    today={qatarToday()}
                    shiftDate={shiftDate}
                />
            ) : loading ? (
                <div className="sal-loading">Loading runs…</div>
            ) : (
                <>
                    <div className="sal-table-wrap">
                        <table className="sal-table">
                            <thead>
                                <tr>
                                    <th />
                                    <th>Type</th>
                                    <th>Started</th>
                                    <th>Duration</th>
                                    <th>Status</th>
                                    <th>Downloads</th>
                                    <th>Uploads</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.length === 0
                                    ? <tr><td colSpan={8} className="sal-empty">No runs recorded yet</td></tr>
                                    : runs.map(r => <RunRow key={r.id} run={r} />)
                                }
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="sal-pagination">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                            <span>{page} / {totalPages}</span>
                            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SalesAutomationLogsTab;
